import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import { EMAIL_REGEX } from '../../../helpers/constants'
import { selectUserData } from '../../../reducers/userDataReducer'
import Dashboard from '../../Dashboard'
import PageHeader from '../common/components/PageHeader'
import Steps from '../common/components/Steps'
import FormNavButtons from '../common/components/FormNavButtons'
import EventLoader from '../common/components/Loader'
import MobilePreview from '../common/components/mobilePreview/MobilePreview'
import { useCreateEvent } from '../common/hooks/useCreateEvent'
import { useEditEvent } from '../common/hooks/useEditEvent'
import { useConfirmationModal } from '../common/hooks/useConfirmationModal'
import { useGetEventById } from '../common/hooks/useGetEventById'
import { isDateNotChanged } from '../common/helpers'

import FirstStep from './FirstStep'
import SecondStep from './SecondStep'
import ThirdStep from './ThirdStep'
import FourthStep from './FourthStep'
import { useEventFormState } from './hooks/useEventFormState'
import { mapFormDataToEventDto } from './mappers'

const EventFormPage = () => {
  const { t } = useTranslation(['eventManagement', 'common'])
  const steps = t('eventManagement:eventFormPage.steps', { returnObjects: true })
  const [currentStep, setCurrentStep] = useState(0)
  const navigate = useNavigate()
  const { eventId } = useParams()

  const userData = useSelector(selectUserData)
  const isPropertyManagerOrFrontDesk = ['PropertyManager', 'ShiftManager'].includes(
    userData.user.role,
  )

  const { event: eventData, isLoading: isEventLoading } = useGetEventById(eventId)

  const { askConfirmation, ConfirmationModal } = useConfirmationModal()
  const { isLoading: isCreateLoading, createEvent } = useCreateEvent({
    eventType: 'Building event',
    redirectFn: () => navigate('/dashboard/calendar'),
  })
  const { isLoading: isEditLoading, editEvent } = useEditEvent({
    eventType: 'Building event',
    redirectFn: () => navigate('/dashboard/calendar'),
  })

  const isLoading = isEventLoading || isCreateLoading || isEditLoading
  const isEditMode = !!eventData

  const {
    initialValues,
    validationSchema,
    buildingOptions,
    eventTypeOptions,
    trainerOptions,
    availableOptions,
    areOptionsLoading,
  } = useEventFormState({
    userData,
    eventData,
    t,
  })

  const submitForm = async (values) => {
    if (isLoading) {
      return
    }

    const formData = await mapFormDataToEventDto(values)

    if (isEditMode) {
      const startTimeNotChanged = isDateNotChanged(values.timeStart, eventData.start)

      const shouldOpenConfirmModal = !!eventData.repeatCorrelationId && startTimeNotChanged

      if (shouldOpenConfirmModal) {
        const confirmed = await askConfirmation()

        if (!confirmed) {
          return
        }

        editEvent(eventData.id, formData, confirmed)
        return
      }

      editEvent(eventData.id, formData, false)
      return
    }

    createEvent(formData)
  }

  const handleStepClick = (clickedStepIndex, stepsValidation) => {
    if (!stepsValidation[clickedStepIndex]) {
      return
    }

    setCurrentStep(clickedStepIndex)
  }

  return (
    <Dashboard.Container className="ctp-event-management">
      <EventLoader isLoading={isLoading || areOptionsLoading} />
      <PageHeader
        title={
          isEditMode
            ? t('eventManagement:eventFormPage.editTitle')
            : t('eventManagement:eventFormPage.createTitle')
        }
      />
      <div className="content-container">
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={submitForm}
          validateOnChange
          validateOnBlur
        >
          {({ setFieldValue, values, resetForm }) => {
            const {
              building,
              type,
              name,
              organizer,
              description,
              image,
              place,
              timeStart,
              timeEnd,
              frequency,
              capacity,
              guestsAmount,
              positiveReview,
              socialLinkService,
              replyTo,
              remindRsvp,
              remindRsvpValues,
              remindAll,
              remindAllValues,
              tickets,
            } = values

            const isTypeValid = isPropertyManagerOrFrontDesk ? true : !!type
            const isOrganizerValid = isPropertyManagerOrFrontDesk ? true : !!organizer
            const isFrequencyValid = isEditMode ? true : !!frequency
            const socialLinkValid = positiveReview ? !!socialLinkService : true

            const isCapacityValid =
              capacity &&
              Number.isInteger(Number(capacity)) &&
              Number(capacity) > 0 &&
              Number(capacity) <= 1000000
            const isGuestAmmountValid =
              capacity > guestsAmount && Number.isInteger(Number(guestsAmount))
            const areTicketsValid = tickets.every((ticket) => !!ticket.name.trim())
            const replyToValid = positiveReview ? !!replyTo && EMAIL_REGEX.test(replyTo) : true
            const isRemindRsvpValid = remindRsvp
              ? !!remindRsvpValues.filter((p) => !!p).length
              : true
            const isRemindAllValid = remindAll ? !!remindAllValues.filter((p) => !!p).length : true

            const stepsValidation = {
              0:
                !!building && isTypeValid && !!name && isOrganizerValid && !!description && !!image,
              1: !!place && !!timeStart && !!timeEnd && !!isFrequencyValid,
              2: isGuestAmmountValid && isCapacityValid && areTicketsValid,
              3: replyToValid && socialLinkValid && isRemindRsvpValid && isRemindAllValid,
            }

            return (
              <>
                <div className="event-form-container">
                  <Steps
                    steps={steps}
                    currentStep={currentStep}
                    handleStepClick={(index) => handleStepClick(index, stepsValidation)}
                  />
                  <div className="event-form-container__form">
                    <FirstStep
                      displayStep={currentStep === 0}
                      isPropertyManagerOrFrontDesk={isPropertyManagerOrFrontDesk}
                      buildingOptions={buildingOptions}
                      eventTypeOptions={eventTypeOptions}
                      trainerOptions={trainerOptions}
                      formValues={values}
                      setFieldValue={setFieldValue}
                    />
                    <SecondStep
                      displayStep={currentStep === 1}
                      formValues={values}
                      setFieldValue={setFieldValue}
                      isEditMode={isEditMode}
                    />
                    <ThirdStep
                      displayStep={currentStep === 2}
                      formValues={values}
                      setFieldValue={setFieldValue}
                      taken={eventData?.taken}
                    />
                    <FourthStep
                      displayStep={currentStep === 3}
                      formValues={values}
                      socialLinksOptions={availableOptions}
                      setFieldValue={setFieldValue}
                    />
                  </div>
                  <FormNavButtons
                    isFirstStep={!currentStep}
                    isLastStep={currentStep + 1 === steps.length}
                    isEditMode={isEditMode}
                    setStep={setCurrentStep}
                    publishButtonLabel={
                      isEditMode
                        ? t('common:actions.saveChanges')
                        : t('eventManagement:eventFormPage.publishButton')
                    }
                    onPublish={() => submitForm(values)}
                    onDiscard={() => resetForm(initialValues)}
                    onSaveDraft={() => {}}
                    nextButtonDisabled={!stepsValidation[currentStep] || isLoading}
                  />
                </div>
                <div className="event-form-mobile-preview-container">
                  <h2>{t('eventManagement:common.mobilePreview')}</h2>
                  <MobilePreview formState={values} eventType="Event" />
                </div>
              </>
            )
          }}
        </Formik>
      </div>
      <ConfirmationModal />
    </Dashboard.Container>
  )
}

export default EventFormPage
